import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import qs from 'query-string';
import { dataCentralApi } from '@/api/client';
import { toFilters } from '@/api/customers';
import type { ResponseArray, ResponseObject } from '@/types/shared';

/**
 * Surveys — endpoints and field names lifted verbatim from the DataCentral v4
 * backend (`modules/survey`). Shapes verified against live staging.
 *
 * Live API: GET /surveys → { results, pagination }
 *           GET /surveys/:id → { message, result }
 *           POST /surveys → 201
 *           POST /surveys/bulk → 201
 *           PATCH /surveys/:id/responses → 200
 *           GET /surveys/customer-ids/current-quarter → { message, result: { customer_ids } }
 */

/* ── Types ─────────────────────────────────────────────────────────────── */

export type UpsalePotential = 'CLOUD' | 'CDN' | 'VNIS';

/**
 * A survey record row returned by list (`GET /surveys`) and detail
 * (`GET /surveys/:id`). Field names verified live — nullable pgtype fields
 * serialize as `null` when unset.
 */
export interface SurveyRecord {
  survey_record_id: number;
  survey_id: number;
  service_name: string;
  customer_id: number;
  customer_name: string;
  employee_id: number;
  employee_name: string;
  score_service_quality: number | null;
  score_customer_care: number | null;
  score_tech_support: number | null;
  score_issue_resolution: number | null;
  score_referral_willingness: number | null;
  score_referral_score: number | null;
  average_score: number | null;
  note_general: string | null;
  note_service_quality: string | null;
  note_customer_care: string | null;
  note_tech_support: string | null;
  note_issue_resolution: string | null;
  note_referral_willingness: string | null;
  improvement: string | null;
  upsale_potential: UpsalePotential[];
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

/** Scores + notes payload for creating/updating a survey record. */
export interface SurveyRecordScores {
  score_service_quality?: number;
  score_customer_care?: number;
  score_tech_support?: number;
  score_issue_resolution?: number;
  score_referral_willingness?: number;
  note_general?: string;
  note_service_quality?: string;
  note_customer_care?: string;
  note_tech_support?: string;
  note_issue_resolution?: string;
  note_referral_willingness?: string;
  improvement?: string;
  upsale_potential?: UpsalePotential[];
}

/** `POST /surveys` payload — one customer + their scores. */
export interface CreateSurveyRecordPayload {
  customer_id: number;
  records: SurveyRecordScores;
}

/** `POST /surveys/bulk` payload — array of records. */
export interface BulkCreateSurveyRecordsPayload {
  records: CreateSurveyRecordPayload[];
}

/** `PATCH /surveys/:id/responses` payload — same shape as scores. */
export type UpdateSurveyRecordPayload = SurveyRecordScores;

/* ── List params ────────────────────────────────────────────────────────── */

export interface SurveyListParams {
  page?: number;
  page_size?: number;
  search?: string;
  /** Structured filters, base64-encoded into the `filters` query param. */
  filters?: Record<string, unknown>;
}

/** Filters accepted by the backend's `SurveyRecordFilters` struct. */
export interface SurveyRecordFilters {
  employee_ids?: number[];
  customer_sale_followers?: number[];
  customer_cs_followers?: number[];
  execution_time_range?: string[];
}

function listQuery(params: SurveyListParams): string {
  const { filters, ...rest } = params;
  const q: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(rest)) {
    if (v !== undefined && v !== '') q[k] = v as string | number;
  }
  if (filters && Object.keys(filters).length > 0) q.filters = toFilters(filters);
  return qs.stringify(q);
}

/* ── Query keys ─────────────────────────────────────────────────────────── */

export const surveyKeys = {
  all: ['surveys'] as const,
  list: (params: SurveyListParams) => ['surveys', 'list', params] as const,
  detail: (id: number) => ['surveys', 'detail', id] as const,
  currentQuarterIds: () => ['surveys', 'current-quarter-ids'] as const,
};

/* ── Queries ─────────────────────────────────────────────────────────────── */

/** `GET /surveys` — paginated list of survey records. */
export function useSurveys(params: SurveyListParams = {}) {
  return useQuery({
    queryKey: surveyKeys.list(params),
    queryFn: async (): Promise<ResponseArray<SurveyRecord>> => {
      const res = await dataCentralApi.get<ResponseArray<SurveyRecord>>(
        `/surveys?${listQuery({ page: 1, page_size: 20, ...params })}`,
      );
      return res.data;
    },
  });
}

/** `GET /surveys/:id` — single survey record detail. */
export function useSurveyRecord(id: number) {
  return useQuery({
    queryKey: surveyKeys.detail(id),
    enabled: id > 0,
    queryFn: async (): Promise<ResponseObject<SurveyRecord>> => {
      const res = await dataCentralApi.get<ResponseObject<SurveyRecord>>(
        `/surveys/${id}`,
      );
      return res.data;
    },
  });
}

/** `GET /surveys/customer-ids/current-quarter` — IDs already surveyed this quarter. */
export function useSurveyedCustomerIds() {
  return useQuery({
    queryKey: surveyKeys.currentQuarterIds(),
    queryFn: async (): Promise<number[]> => {
      const res = await dataCentralApi.get<ResponseObject<{ customer_ids: number[] }>>(
        '/surveys/customer-ids/current-quarter',
      );
      return res.data.result.customer_ids ?? [];
    },
  });
}

/* ── Mutations ────────────────────────────────────────────────────────────── */

/**
 * `POST /surveys/bulk` — create multiple survey records in a single DB
 * transaction. If any record fails, none are saved. The USER triggers this.
 */
export function useCreateSurveyRecordsBulk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: BulkCreateSurveyRecordsPayload) => {
      const res = await dataCentralApi.post<ResponseObject<unknown>>(
        '/surveys/bulk',
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.all });
    },
  });
}

/**
 * `PATCH /surveys/:id/responses` — update scores/notes for an existing record.
 * The USER triggers this.
 */
export function useUpdateSurveyRecord(id: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: UpdateSurveyRecordPayload) => {
      const res = await dataCentralApi.patch<ResponseObject<unknown>>(
        `/surveys/${id}/responses`,
        payload,
      );
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: surveyKeys.detail(id) });
      qc.invalidateQueries({ queryKey: surveyKeys.all });
    },
  });
}
